/**
 * mySchedule.ts — 내 업무 홈 "생산 일정" 요약 (B′ 미리보기, 2026-10-19).
 *
 * 한 날짜 = 한 줄: "현장A 외 N건 · 총 000㎥(톤)" — 예정(planned)이며 실적이 아니다.
 *
 * 중앙(레미콘) 데이터 소스
 *   - 현장 단위: daily_shipment(단수, RQC raw) 의 site·qty(= 예정 yejung qty).
 *       cron(rqcScheduledSync)이 오늘 −2 ~ +7 영업일을 미리 적재한다(2026-10-19 범위 확대, 운영 반영 전 —
 *       반영 전에는 +3 영업일 이후가 비어 "ERP 예정 없음"으로 보인다).
 *   - 날짜 합계: daily_shipments(복수) 의 gov_planned_m3 + cust_planned_m3.
 *       오늘은 rqcWatchToday 가 5분마다 갱신해 단수보다 최신이다.
 *   - 규칙: 합계 = 복수 예정 합(있으면) ?? 단수 qty 합. 현장 이름·건수 = 단수(있을 때만).
 * 경원(아스콘): OKIS 미러 수주(tenants/gyeongwon/units/ascon/ascon_orders, 2026-10-22 연결) → asconProdDays (톤).
 *   오늘·앞날 = 수주량 합(수주는 예정 자리 — 실적으로 쓰지 않는다), 지난 날 = 출하누계 합(계근 실중량, 실적).
 *
 * 순수 함수만. Firebase import 금지(domain 규칙).
 */
import { isWorkday, type WorkdayConfig } from "./workday";
import type { TenantKey } from "./space";
import type { AsconOrder } from "./asconShipment";

export type ProdUnit = "m3" | "ton";

export interface ProdDay {
  tenant: TenantKey;
  date: string;
  unit: ProdUnit;
  /** 대표 현장(예정 물량이 가장 큰 곳). 현장 단위 자료가 없으면 undefined */
  topSite?: string;
  /** 현장 수(같은 현장의 여러 규격은 1건). 현장 단위 자료가 없으면 0 */
  siteCount: number;
  /** 예정 합계 */
  total: number;
  /** true = 가상 데이터 */
  mock?: boolean;
  /** 경원만: plan = 수주량(오늘·앞날) / actual = 출하누계(지난 날). 없으면 예정 */
  kind?: "plan" | "actual";
}

/** 현장 단위 예정 1행 (RqcShipment 에서 필요한 것만) */
export interface SitePlanRow {
  date: string;
  site: string;
  company?: string;
  qty: number;
}

/** today 다음 영업일부터 영업일 n개 (today 제외) */
export function nextWorkdays(today: string, n: number, config: WorkdayConfig): string[] {
  const out: string[] = [];
  const [y, m, d] = today.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  for (let i = 0; i < 400 && out.length < n; i++) {
    dt.setUTCDate(dt.getUTCDate() + 1);
    const s = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
    if (isWorkday(s, config)) out.push(s);
  }
  return out;
}

/** 한 날짜의 현장 행 → 대표 현장·현장 수·합계 */
export function summarizeSites(rows: SitePlanRow[]): { topSite?: string; siteCount: number; total: number } {
  const bySite = new Map<string, number>();
  for (const r of rows) {
    if (!(r.qty > 0)) continue;
    const name = (r.site || r.company || "").trim() || "(현장 미상)";
    bySite.set(name, (bySite.get(name) ?? 0) + r.qty);
  }
  let topSite: string | undefined;
  let topQty = -1;
  let total = 0;
  for (const [name, q] of bySite) {
    total += q;
    if (q > topQty) { topQty = q; topSite = name; }
  }
  return { topSite, siteCount: bySite.size, total: Math.round(total * 10) / 10 };
}

/**
 * 중앙 날짜별 요약. 현장 행도 예정 합계도 없는 날은 결과에 넣지 않는다(= "자료 없음", 0 이 아니다).
 * @param plannedTotals 날짜 → daily_shipments 예정 합(필드가 없으면 키 부재)
 */
export function buildJoongangDays(
  dates: string[],
  siteRows: SitePlanRow[],
  plannedTotals: ReadonlyMap<string, number>,
): Map<string, ProdDay> {
  const rowsByDate = new Map<string, SitePlanRow[]>();
  for (const r of siteRows) {
    const list = rowsByDate.get(r.date);
    if (list) list.push(r);
    else rowsByDate.set(r.date, [r]);
  }
  const out = new Map<string, ProdDay>();
  for (const date of dates) {
    const s = summarizeSites(rowsByDate.get(date) ?? []);
    const planned = plannedTotals.get(date);
    const total = planned != null && planned > 0 ? planned : s.total;
    if (!(total > 0)) continue;
    out.set(date, { tenant: "joongang", date, unit: "m3", topSite: s.topSite, siteCount: s.siteCount, total });
  }
  return out;
}

/**
 * 경원 날짜별 요약(날짜순). 오늘·앞날 = 수주량 합(kind plan), 지난 날 = 출하누계 합(kind actual).
 * 지난 날 출하 0 인 날은 넣지 않는다. 앞날 수주량 0(미정)은 넣는다 — 화면은 "수주량 미정".
 * 대표 현장 = 그날 물량이 가장 큰 현장(같은 거래처·현장 여러 규격은 1건).
 */
export function asconProdDays(
  orders: Pick<AsconOrder, "date" | "custCode" | "siteCode" | "site" | "company" | "orderT" | "shippedT">[],
  today: string,
  mock = false,
): ProdDay[] {
  const byDate = new Map<string, typeof orders>();
  for (const o of orders) {
    if (!o.date) continue;
    const list = byDate.get(o.date);
    if (list) list.push(o);
    else byDate.set(o.date, [o]);
  }
  const out: ProdDay[] = [];
  for (const date of [...byDate.keys()].sort()) {
    const plan = date >= today;
    const qtyOf = (o: (typeof orders)[number]) => (plan ? o.orderT : o.shippedT);
    const bySite = new Map<string, { name: string; qty: number }>();
    for (const o of byDate.get(date)!) {
      const k = `${o.custCode}_${o.siteCode}`;
      const cur = bySite.get(k) ?? { name: (o.site || o.company || "").trim() || "(현장 미상)", qty: 0 };
      cur.qty += qtyOf(o);
      bySite.set(k, cur);
    }
    const sites = [...bySite.values()];
    const total = Math.round(sites.reduce((a, x) => a + x.qty, 0) * 10) / 10;
    if (!plan && !(total > 0)) continue;
    const top = sites.reduce((a, x) => (x.qty > a.qty ? x : a), sites[0]);
    out.push({
      tenant: "gyeongwon", date, unit: "ton", topSite: top?.name, siteCount: sites.length, total,
      kind: plan ? "plan" : "actual", ...(mock ? { mock } : {}),
    });
  }
  return out;
}

const UNIT_LABEL: Record<ProdUnit, string> = { m3: "㎥", ton: "톤" };

/** "현장A 외 2건 · 총 1,240㎥" / 현장 자료 없으면 "총 1,240㎥" */
export function prodLineText(p: ProdDay): { site: string | null; total: string } {
  // 경원 앞날 수주는 수주량을 0 으로 두는 일이 많다(미정, 10-22 실데이터) — 0톤 대신 "수주량 미정"
  const total = p.total > 0
    ? `총 ${p.total.toLocaleString("ko-KR", { maximumFractionDigits: 1 })}${UNIT_LABEL[p.unit]}`
    : "수주량 미정";
  if (!p.topSite) return { site: null, total };
  return { site: p.siteCount > 1 ? `${p.topSite} 외 ${p.siteCount - 1}건` : p.topSite, total };
}

const WEEK = ["일", "월", "화", "수", "목", "금", "토"] as const;

/** "10/20 (화)" */
export function shortDateLabel(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${m}/${d} (${WEEK[dow]})`;
}

/** "10월 19일 월요일" */
export function longDateLabel(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${m}월 ${d}일 ${WEEK[dow]}요일`;
}
